export enum ModificationType {
  SIMPLIFY = 'simplify',
  MAKE_CHALLENGING = 'make_challenging',
  FIX_ERRORS = 'fix_errors',
  REWRITE = 'rewrite',
  LONGER_TEXT = 'longer_text',
  TECHNICAL = 'technical',
  CREATIVE = 'creative',
  FORMAL = 'formal',
  FRIENDLY = 'friendly',
}

export enum Target {
  QUESTION = 'question_text',
  OPTION = 'option_text',
  PAIR_LEFT = 'pair_left',
  PAIR_RIGHT = 'pair_right',
}

export enum QuestionType {
  MULTIPLE_CHOICE = 'multiple_choice',
  MULTIPLE_RESPONSE = 'multiple_response',
  /**
   * API wire value is 'rearrange'. The domain concept is "sequences" (ordered arrangement questions);
   * the backend expects 'rearrange' as the question_type value.
   */
  SEQUENCES = 'rearrange',
  TRUE_FALSE = 'true_false',
  FILL_IN_THE_BLANKS = 'fill_in_the_blanks',
  SCENARIO = 'scenario',
  MATCHING = 'matching',
}

export enum DifficultyLevel {
  EASY = 'easy',
  MEDIUM = 'medium',
  HARD = 'hard',
}

export enum Language {
  ENGLISH = 'english',
  NORSK_BOKMAL = 'norsk_bokmal',
  NORSK_NYNORSK = 'norsk_nynorsk',
}

export enum DALLE_ART_STYLES {
  auto = 'auto',
  digital_art = 'digital_art',
  graphic_design = 'graphic_design',
  illustration = 'illustration',
  street_art = 'street_art',
  _3d_art = '_3d_art',
  cyberpunk = 'cyberpunk',
  vaporwave = 'vaporwave',
  pop_art = 'pop_art',
  pixel_art = 'pixel_art',
  surrealism = 'surrealism',
  fantasy = 'fantasy',
  neon_punk = 'neon_punk',
  new_media_art = 'new_media_art',
  sticker_illustration = 'sticker_illustration',
  storybook = 'storybook',
  cybernetic = 'cybernetic',
  isometric_3d = 'isometric_3d',
  claymation = 'claymation',
  minimalism = 'minimalism',
  pop_surrealism = 'pop_surrealism',
  abstract = 'abstract',
  abstract_expressionism = 'abstract_expressionism',
  watercolor = 'watercolor',
  watercolor_and_pen = 'watercolor_and_pen',
  ballpoint_pen = 'ballpoint_pen',
  comic_book = 'comic_book',
  stencil = 'stencil',
  low_poly = 'low_poly',
  glitch = 'glitch',
  mixed_media = 'mixed_media',
  charcoal_sketch = 'charcoal_sketch',
  pencil_drawing = 'pencil_drawing',
  botanical_illustration = 'botanical_illustration',
  art_deco = 'art_deco',
  art_nouveau = 'art_nouveau',
  futurism = 'futurism',
  steampunk = 'steampunk',
  constructivist = 'constructivist',
  fauvism = 'fauvism',
  neo_pop_art = 'neo_pop_art',
  memphis = 'memphis',
  biopunk = 'biopunk',
  vector_art = 'vector_art',
  neo_expressionism = 'neo_expressionism',
  layered_paper = 'layered_paper',
  pastels = 'pastels',
  scratch_art = 'scratch_art',
  houdini_3d = 'houdini_3d',
  geometric_abstraction = 'geometric_abstraction',
  op_art = 'op_art',
  suprematism = 'suprematism',
  mexican_muralism = 'mexican_muralism',
  modernism = 'modernism',
  dada = 'dada',
  cubism = 'cubism',
  alegria_corporate_memphis = 'alegria_corporate_memphis',
  color_field_painting = 'color_field_painting',
  renaissance = 'renaissance',
  rococo = 'rococo',
  bauhaus = 'bauhaus',
  psychedelic = 'psychedelic',
  impressionism = 'impressionism',
  voroni_diagram = 'voroni_diagram',
  felt_pieces = 'felt_pieces',
  woodcut = 'woodcut',
  realistic_photography = 'realistic_photography',
}

export enum AspectRatio {
  SQUARE = 'square',
  LANDSCAPE = 'landscape',
  PORTRAIT = 'portrait',
}

export type PageContent = {
  text: string;
};

export type NanoId = {
  nano_id: number;
  page_ids?: string[];
};

export type Answer = {
  id: string;
  text: string;
  isCorrect?: boolean;
};

export type BlankOptionApi = {
  id: string;
  text: string;
  isCorrect: boolean;
};

export type BlankApi = {
  id: string;
  blank_type: 'dropdown' | 'text';
  options?: BlankOptionApi[];
  accepted_answers?: string[];
};

export type FillInTheBlanksAnswer = {
  blanks: BlankApi[];
};

export type BlankAnswer = {
  id: string;
  answers: string[];
};

type BaseApiQuestion = {
  question_id: number;
  question_text: string;
  difficulty: DifficultyLevel;
  knowledge: string;
  instructionText?: string;
};

export type StandardApiQuestion = BaseApiQuestion & {
  question_type:
    | QuestionType.MULTIPLE_CHOICE
    | QuestionType.MULTIPLE_RESPONSE
    | QuestionType.TRUE_FALSE
    | QuestionType.SEQUENCES
    // Scenario is a standard API question — wire format is identical to MCQ; the
    // discriminator alone distinguishes it. Diverge only if scenario gains its
    // own answer shape (e.g., a separate scenario field).
    | QuestionType.SCENARIO;
  answers: Answer[];
};

type FitbApiQuestion = BaseApiQuestion & {
  question_type: QuestionType.FILL_IN_THE_BLANKS;
  answers: [FillInTheBlanksAnswer];
};

export type MatchingPair = { id: string; left_text: string; right_text: string };

type MatchingApiQuestion = BaseApiQuestion & {
  question_type: QuestionType.MATCHING;
  answers: MatchingPair[];
};

export type Question = StandardApiQuestion | FitbApiQuestion | MatchingApiQuestion;

export const isFitbApiQuestion = (q: Question): q is FitbApiQuestion =>
  q.question_type === QuestionType.FILL_IN_THE_BLANKS;

export const isMatchingApiQuestion = (q: Question): q is MatchingApiQuestion =>
  q.question_type === QuestionType.MATCHING;

type QuestionSummary = {
  question_id: number;
  question_text: string;
}

export type QuizRequest = {
  pages?: PageContent[];
  num_questions: number;
  question_type: QuestionType;
  difficulty: DifficultyLevel;
  num_options: number;
  language?: Language;
  allow_multiple_correct?: boolean;
  nano_ids?: NanoId[];
  previous_questions?: QuestionSummary[];
};

export type QuizResponse = {
  questions: Question[];
  total_questions: number;
  metadata: Record<string, any>;
};

export type RewriteQuestionOrOptionRequest = {
  text: string;
  modification_type: ModificationType;
  target: Target;
  isCorrect?: boolean;
  question_text?: string;
  knowledge: string;
  question_type?: QuestionType;
  blank_answers?: BlankAnswer[];
  paired_text?: string;
};

export type RewriteQuestionOrOptionResponse = {
  text: string;
};

export type AddNewOptionRequest = {
  question: Question;
  instruction?: string;
  isCorrect: boolean;
  blank_id?: string;
};

export type OptionForImageGeneration = Answer | FillInTheBlanksAnswer | MatchingPair;

export type GenerateImageRequest = {
  question_id: number;
  question_text: string;
  answers: OptionForImageGeneration[];
  style?: DALLE_ART_STYLES;
  aspect_ratio?: AspectRatio;
  knowledge: string;
}[];

export type GenerateImageResponse = {
  image: string;
  question_id: number;
}[];
